<template>
  <section
    class="category-selector"
    aria-labelledby="category-selector-title"
  >
    <h4
      id="category-selector-title"
      class="mb-3"
    >
      Sélection des catégories d'offre spéciale
    </h4>
    <v-btn-toggle
      :value="view"
      class="category-view-switch mb-4"
      aria-label="Vue des catégories"
      mandatory
      group
      color="primary"
      @change="changeView"
    >
      <v-btn
        value="available"
        class="category-view-switch__tab"
        small
        :ripple="false"
        :disabled="disabled"
      >
        Catégories
      </v-btn>
      <v-btn
        value="selected"
        class="category-view-switch__tab"
        small
        :ripple="false"
        :disabled="disabled"
      >
        Sélectionnées
        <span class="category-view-switch__count">{{ selectedIds.length }}</span>
      </v-btn>
    </v-btn-toggle>

    <v-text-field
      v-model="search"
      label="Rechercher par libellé"
      outlined
      dense
      clearable
      hide-details
      maxlength="120"
      :disabled="disabled"
    />
    <p class="text-caption mt-3 mb-3">
      {{ view === 'selected'
        ? 'Toutes les catégories cochées restent accessibles, même anciennes ou sans offre.'
        : 'Uniquement les catégories avec au moins une période de séjour en cours ou à venir.' }}
      Les associations seront enregistrées avec le formulaire.
      Cocher un groupe sélectionne toutes ses catégories correspondant aux filtres, même celles du détail non affiché.
    </p>

    <v-alert
      v-if="error"
      type="error"
      text
      dense
      role="alert"
    >
      {{ error }} Votre sélection est conservée.
      <v-btn
        small
        text
        :disabled="disabled"
        @click="load"
      >
        Réessayer
      </v-btn>
    </v-alert>
    <div :aria-busy="loading ? 'true' : 'false'">
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
      />
      <p
        v-if="!loading && !error && !items.length"
        class="pa-4 mb-0 text-center"
      >
        {{ view === 'selected' && !selectedIds.length ? 'Aucune catégorie sélectionnée.' : 'Aucune catégorie ne correspond à ces critères.' }}
      </p>
      <CategorySelectionGroup
        v-for="item in items"
        :key="item.id"
        :group="item"
        :selected-ids="selectedIds"
        :super-category-id="superCategoryId"
        :disabled="disabled || loading"
        @change="toggleMany($event.ids, $event.checked)"
      />
    </div>
    <div
      v-if="!error"
      class="mt-3"
    >
      <p
        class="text-caption text-center mb-1"
        aria-live="polite"
      >
        {{ loading ? 'Chargement des catégories…' : total + ' libellé(s) · ' + totalCategories + ' catégorie(s) · ' + selectedIds.length + ' sélectionnée(s)' }}
      </p>
      <v-pagination
        v-if="pageCount > 1"
        :value="page"
        :length="pageCount"
        :total-visible="5"
        :disabled="disabled || loading"
        @input="changePage"
      />
    </div>
  </section>
</template>

<script>
import config from '@/config'
import CategorySelectionGroup from '@/components/SpecialOffer/CategorySelectionGroup.vue'

export default {
  components: { CategorySelectionGroup },
  props: {
    value: { type: Array, default: () => [] },
    superCategoryId: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    refresh: { type: Number, default: 0 }
  },
  data () {
    return {
      view: 'available',
      search: '',
      page: 1,
      limit: 10,
      total: 0,
      totalCategories: 0,
      items: [],
      loading: false,
      error: null,
      requestId: 0,
      timer: null
    }
  },
  computed: {
    selectedIds () {
      return [...new Set(this.value)]
    },
    pageCount () {
      return Math.max(1, Math.ceil(this.total / this.limit))
    }
  },
  watch: {
    search () { this.resetSearch(300) },
    value () {
      if (this.view === 'selected') this.scheduleLoad()
    },
    refresh () { this.scheduleLoad() }
  },
  created () {
    this.load()
  },
  beforeDestroy () {
    clearTimeout(this.timer)
    ++this.requestId
  },
  methods: {
    resetSearch (delay = 0) {
      this.page = 1
      this.scheduleLoad(delay)
    },
    scheduleLoad (delay = 0) {
      clearTimeout(this.timer)
      ++this.requestId
      this.loading = true
      this.timer = setTimeout(() => this.load(), delay)
    },
    changeView (view) {
      if (this.disabled || !view || view === this.view) return
      this.view = view
      this.search = ''
      this.resetSearch()
    },
    changePage (page) {
      this.page = page
      this.scheduleLoad()
    },
    toggle (id, checked) {
      this.toggleMany([id], checked)
    },
    toggleMany (categoryIds, checked) {
      if (this.disabled || this.loading) return
      const ids = new Set(this.selectedIds)
      categoryIds.forEach(id => {
        if (checked) ids.add(id)
        else ids.delete(id)
      })
      this.$emit('input', [...ids])
    },
    async load () {
      clearTimeout(this.timer)
      const requestId = ++this.requestId
      this.loading = true
      this.error = null
      try {
        const response = await this.$http.post(config.apiUrl + '/api/special-offer/categories/selection', {
          page: this.page,
          limit: this.limit,
          search: (this.search || '').trim(),
          includeInactive: false,
          selectedOnly: this.view === 'selected',
          grouped: true,
          selectedIds: this.view === 'selected' ? this.selectedIds : []
        }, {
          headers: { Authorization: 'Bearer ' + this.$store.getters.getToken }
        })
        if (requestId !== this.requestId) return
        if (!response.data || !Array.isArray(response.data.items) || !Number.isInteger(response.data.total) || response.data.total < 0
          || !Number.isInteger(response.data.totalCategories) || response.data.totalCategories < 0
          || response.data.items.some(group => !Array.isArray(group.categories) || group.categories.length !== group.categoryCount)
          || !Number.isInteger(response.data.page) || response.data.page < 1) {
          throw new Error('Invalid category response')
        }
        this.items = response.data.items
        this.total = response.data.total
        this.totalCategories = response.data.totalCategories
        this.page = response.data.page
      } catch (error) {
        if (requestId !== this.requestId) return
        this.items = []
        this.error = 'Impossible de charger les catégories.'
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.category-selector {
  border: 1px solid rgba(94, 86, 105, 0.18);
  border-radius: 6px;
  padding: 16px;
}

.category-view-switch {
  display: flex;
  gap: 8px;
  border-bottom: 1px solid rgba(94, 86, 105, 0.14);
}

.category-view-switch > .category-view-switch__tab.v-btn {
  height: 44px;
  margin: 0;
  padding: 0 12px;
  border-width: 0 0 2px;
  border-color: transparent;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none;
  opacity: 1;
}

.category-view-switch > .category-view-switch__tab.v-btn--active {
  border-bottom-color: currentColor;
}

.category-view-switch > .category-view-switch__tab::before {
  opacity: 0;
}

.category-view-switch > .category-view-switch__tab:hover::before {
  opacity: 0.04;
}

.category-view-switch > .category-view-switch__tab:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: -4px;
}

.category-view-switch__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 10px;
  background: rgba(94, 86, 105, 0.08);
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
</style>
